import { createFileRoute } from "@tanstack/react-router";
import { ConsentSettingsButton } from "@/components/ConsentBanner";
import { useT } from "@/lib/i18n";
import { hreflangLinks } from "@/lib/seo";
import { pageTitle } from "@/lib/brand";
import { PageIntro, PageSection, Prose, MoreLink } from "@/components/site/page";

const SITE_URL = "https://akillisurdurulebilirlik.com";

export const Route = createFileRoute("/privacy")({
  component: PrivacyPage,
  head: () => ({
    meta: [
      { title: pageTitle("Gizlilik Politikası", "tr") },
      {
        name: "description",
        content:
          "Akıllı Sürdürülebilirlik gizlilik politikası: hangi kişisel verileri topluyoruz, nasıl saklıyoruz, KVKK ve GDPR kapsamındaki haklarınız.",
      },
      { property: "og:title", content: "Gizlilik Politikası — Akıllı Sürdürülebilirlik" },
      { property: "og:description", content: "KVKK ve GDPR kapsamında veri sorumlusu, toplanan veriler ve kullanıcı hakları." },
      { property: "og:url", content: `${SITE_URL}/privacy` },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "tr_TR" },
      { property: "og:locale:alternate", content: "en_US" },
      { name: "robots", content: "index,follow" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: `${SITE_URL}/privacy` },
      ...hreflangLinks("/privacy", "/privacy-policy"),
    ],
  }),
});

function PrivacyPage() {
  const { lang } = useT();
  const tr = lang === "tr";

  return (
    <div>
      <PageIntro
        crumbs={[{ label: tr ? "Gizlilik Politikası" : "Privacy Policy" }]}
        kicker={tr ? "Son güncelleme: 3 Kasım 2026" : "Last updated: November 3, 2026"}
        title={tr ? "Gizlilik Politikası" : "Privacy Policy"}
      />

      <PageSection title={tr ? "1. Veri sorumlusu" : "1. Data controller"}>
        <Prose>
          <p>
            {tr
              ? "Bu site (akillisurdurulebilirlik.com) Arda Öztaşkın tarafından kişisel arşiv ve yayın amacıyla işletilmektedir. KVKK ve GDPR kapsamında veri sorumlusu Arda Öztaşkın'dır."
              : "This site (akillisurdurulebilirlik.com) is operated by Arda Öztaşkın as a personal archive and publication. Under GDPR and Turkey's KVKK, the data controller is Arda Öztaşkın."}
          </p>
        </Prose>
      </PageSection>

      <PageSection title={tr ? "2. Topladığımız veriler" : "2. What we collect"}>
        <Prose>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              {tr
                ? "Bülten aboneliği: e-posta adresiniz ve tercih ettiğiniz dil (TR/EN). Yalnızca bülten formunu kendi iradenizle doldurduğunuzda toplanır."
                : "Newsletter signup: your email address and preferred language (TR/EN). Collected only when you submit the newsletter form."}
            </li>
            <li>
              {tr
                ? "Çerez/izleme: Her bölgede analitik ve pazarlama ayrı izinlere bağlıdır ve başlangıçta kapalıdır. Analitik izniyle Google Analytics, Google’a sayfa adresi, tarayıcı/cihaz bilgisi ve ziyaret etkileşimlerini kullanım ölçümü için gönderir; site içi ziyaret ölçümü de bu izne bağlıdır. Pazarlama izniyle Meta Pixel, Meta Platforms’a sayfa görüntülemelerini ve tamamlanan bülten kayıtlarını reklam ölçümü ve optimizasyonu amacıyla bildirir; e-posta adresiniz veya müşteri eşleştirme verileri gönderilmez. Yalnız analitik izni Meta Pixel’i açmaz. Tercihiniz birinci taraf consent çerezinde ve tarayıcı depolamasında 12 ay saklanır; ziyaretçi referansı, tarih, bildirim sürümü, kategoriler ve varsa önceki kabul kaydı tutulur. Çerez tercihleri bağlantısından istediğiniz zaman değiştirebilir veya geri çekebilirsiniz; geri çekme yeni ölçümü durdurur ve izleme çerezlerini siler."
                : "Analytics and marketing are off until separately accepted in every region. Analytics shares page and device/interaction data with Google Analytics for measurement. Marketing shares page views and completed newsletter signups (no email) with Meta Platforms for ad measurement and optimization. Choices and acceptance evidence are stored for 12 months. Cookie preferences let you withdraw at any time."}
            </li>
            <li>
              <ConsentSettingsButton label={tr ? "Çerez tercihleri" : "Cookie settings"} />
            </li>
            <li>
              {tr
                ? "Sunucu kayıtları: barındırma sağlayıcısı, hizmetin sürdürülebilmesi için kısa süreli teknik kayıtlar (IP, kullanıcı aracısı) tutabilir."
                : "Server logs: the hosting provider may keep short-term technical logs (IP, user-agent) needed to operate the service."}
            </li>
          </ul>
        </Prose>
      </PageSection>

      <PageSection title={tr ? "3. Verileri nasıl kullanıyoruz" : "3. How we use your data"}>
        <Prose>
          <p>
            {tr
              ? "E-posta adresiniz yalnızca size yeni yazılardan oluşan bülteni göndermek için kullanılır. Üçüncü taraflarla paylaşılmaz, satılmaz veya pazarlama amacıyla kiralanmaz."
              : "Your email is used only to send you the newsletter of new essays. It is not shared, sold or rented to third parties for marketing."}
          </p>
        </Prose>
      </PageSection>

      <PageSection title={tr ? "4. Saklama ve altyapı" : "4. Storage and infrastructure"}>
        <Prose>
          <p>
            {tr
              ? "Abonelik verileri Lovable Cloud (Supabase altyapısı) üzerinde, satır düzeyinde güvenlik (RLS) politikaları ile şifrelenmiş olarak saklanır. Yalnızca site sahibi okuyabilir. Site Lovable üzerinde barındırılır, görseller WordPress CDN (i0.wp.com) üzerinden sunulur."
              : "Subscriber data is stored on Lovable Cloud (Supabase-based) with row-level security (RLS) policies and only the site owner can read it. The site is hosted on Lovable; images are served via the WordPress CDN (i0.wp.com)."}
          </p>
        </Prose>
      </PageSection>

      <PageSection title={tr ? "5. KVKK / GDPR haklarınız" : "5. Your KVKK / GDPR rights"}>
        <Prose>
          <p>
            {tr
              ? "Verilerinize erişme, düzeltme, silme, işlemeyi kısıtlama ve verilerinizin taşınabilirliğini talep etme hakkına sahipsiniz. Bültenden çıkmak veya verinizin silinmesini istemek için aşağıdaki e-posta adresine yazmanız yeterlidir; talebiniz makul bir süre içinde karşılanır."
              : "You have the right to access, correct, delete, restrict processing of and request portability of your data. To unsubscribe or have your data deleted, email the address below; we'll act on your request within a reasonable time."}
          </p>
        </Prose>
      </PageSection>

      <PageSection title={tr ? "6. İletişim" : "6. Contact"}>
        <Prose>
          <p>
            {tr ? "E-posta: " : "Email: "}
            <a href="mailto:ardaoztaskin@gmail.com">ardaoztaskin@gmail.com</a>
          </p>
        </Prose>
      </PageSection>

      <PageSection>
        <MoreLink to="/">{tr ? "Anasayfa" : "Home"}</MoreLink>
      </PageSection>
    </div>
  );
}
